import { MenuClose, PersonSearch } from '@fs/zion-icon'
import { Button, Cell, Row, TextArea, TextField, useOverlay } from '@fs/zion-ui'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../../hooks/useNotifications'
import { searchForPerson } from '../../../../services/famousHistoricalService'
import { deepCopy, useKeypress } from '../../../../util/Utils'
import HistoricalSearchFiltersButton from './HistoricalSearchFiltersButton'
import HistoricalSearchResultsDialog from './HistoricalSearchResultsDialog'

const HistoricalSearch = ({ countries }) => {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const resultsOpener = useOverlay()
  const [givenName, setGivenName] = useState()
  const [surname, setSurname] = useState()
  const [personId, setPersonId] = useState()
  const [reason, setReason] = useState()
  const [startBirthYearItem, setStartBirthYearItem] = useState()
  const [endBirthYearItem, setEndBirthYearItem] = useState()
  const [useBirthYearRange, shouldUseBirthYearRange] = useState(false)
  const [startDeathYearItem, setStartDeathYearItem] = useState()
  const [endDeathYearItem, setEndDeathYearItem] = useState()
  const [useDeathYearRange, shouldUseDeathYearRange] = useState(false)
  const [country, setCountry] = useState()
  const [persons, setPersons] = useState([])
  const searchBtnDisabled = submitBtnShouldBeDisabled()
  const clearBtnDisabled = !(
    givenName ||
    surname ||
    personId ||
    reason ||
    startBirthYearItem ||
    endBirthYearItem ||
    startDeathYearItem ||
    endDeathYearItem ||
    country ||
    useBirthYearRange ||
    useDeathYearRange
  )

  useEffect(() => {
    if (!resultsOpener.isOpen && !resultsOpener.transitioning) {
      setPersons([])
    }
  }, [resultsOpener.isOpen, resultsOpener.transitioning])

  const searchBtnOnClickHandler = useCallback(async () => {
    try {
      const params = {
        PID: personId,
        'given-name': givenName,
        surname,
        reason,
        'birth-year': useBirthYearRange
          ? `${startBirthYearItem?.formattedDate}-${endBirthYearItem?.formattedDate}`
          : startBirthYearItem?.formattedDate,
        'death-year': useDeathYearRange
          ? `${startDeathYearItem?.formattedDate}-${endDeathYearItem?.formattedDate}`
          : startDeathYearItem?.formattedDate,
        country,
      }
      const matchPersonResponse = await searchForPerson(params)
      const historicalPersons = matchPersonResponse.data.personDtos.filter(
        (person) => person.matchType === 'HISTORICAL'
      )
      setPersons(historicalPersons)
      resultsOpener.open()
    } catch (err) {
      error(
        t('tools.errors.searchingForHistoricalPersons', {
          defaultValue: `Error searching for historical persons: ${err.message || ''}`,
          message: err.message || '',
        }),
        err
      )
    }
  }, [
    country,
    endBirthYearItem,
    endDeathYearItem,
    error,
    givenName,
    personId,
    reason,
    resultsOpener,
    startBirthYearItem,
    startDeathYearItem,
    surname,
    useBirthYearRange,
    useDeathYearRange,
    t,
  ])

  const onEnterKeyPress = useCallback(() => {
    if (!searchBtnDisabled && persons.length === 0) {
      searchBtnOnClickHandler()
    }
  }, [persons.length, searchBtnDisabled, searchBtnOnClickHandler])

  useKeypress('Enter', onEnterKeyPress)

  const removePersonOnDelete = (person) => {
    const index = persons.indexOf(person)
    if (index > -1) {
      persons.splice(index, 1)
    }
    if (persons.length === 0) {
      resultsOpener.close()
    }
  }

  const updatePersonOnEdit = (pId, personJSON, newCountry) => {
    const personsDeepCopy = deepCopy(persons)
    const personToEdit = personsDeepCopy.find((person) => person.id === pId)
    personToEdit.personJSON = deepCopy(personJSON)
    personToEdit.country = newCountry
    setPersons(personsDeepCopy)
  }

  const clearBtnOnClickHandler = () => {
    setGivenName()
    setSurname()
    setReason()
    setPersonId()
    setStartBirthYearItem()
    setEndBirthYearItem()
    shouldUseBirthYearRange(false)
    setStartDeathYearItem()
    setEndDeathYearItem()
    shouldUseDeathYearRange(false)
    setCountry()
  }

  const onFilterOrFieldChange = (fieldType, newValue) => {
    switch (fieldType) {
      case 'givenName': {
        setGivenName(newValue)
        break
      }
      case 'surname': {
        setSurname(newValue)
        break
      }
      case 'personId': {
        setPersonId(newValue)
        break
      }
      case 'reason': {
        setReason(newValue)
        break
      }
      case 'startBirthYearItem': {
        if (newValue === '') {
          setStartBirthYearItem()
          break
        }
        setStartBirthYearItem(newValue)
        break
      }
      case 'endBirthYearItem': {
        setEndBirthYearItem(newValue)
        break
      }
      case 'shouldUseBirthYearRange': {
        shouldUseBirthYearRange(newValue)
        break
      }
      case 'startDeathYearItem': {
        if (newValue === '') {
          setStartDeathYearItem()
          break
        }
        setStartDeathYearItem(newValue)
        break
      }
      case 'endDeathYearItem': {
        setEndDeathYearItem(newValue)
        break
      }
      case 'shouldUseDeathYearRange': {
        shouldUseDeathYearRange(newValue)
        break
      }
      case 'country': {
        if (newValue === '') {
          setCountry()
          break
        }
        setCountry(newValue)
        break
      }
      default: {
        break
      }
    }
  }

  function submitBtnShouldBeDisabled() {
    let submitBtnDisabled = !(givenName || surname || personId || reason)
    if (!submitBtnDisabled) {
      if (useBirthYearRange) {
        submitBtnDisabled = !startBirthYearItem || !endBirthYearItem
      }
      if (useDeathYearRange) {
        submitBtnDisabled = !startDeathYearItem || !endDeathYearItem
      }
    }
    return submitBtnDisabled
  }

  function fields() {
    return (
      <Row>
        <TextField
          id="givenName"
          label={t('givenName', 'Given Name')}
          type="text"
          value={givenName}
          onChange={(e) => onFilterOrFieldChange('givenName', e.target.value)}
          disabled={!!personId}
        />
        <TextField
          id="surname"
          label={t('name.parts.surname', 'Surname')}
          type="text"
          value={surname}
          onChange={(e) => onFilterOrFieldChange('surname', e.target.value)}
          disabled={!!personId}
        />
        <TextArea
          label={t('reason', 'Reason')}
          value={reason}
          onChange={(e) => onFilterOrFieldChange('reason', e.target.value)}
          disableResize
          disabled={!!personId}
        />
        <TextField
          id="personId"
          label={t('personId', 'Person ID')}
          type="text"
          value={personId}
          onChange={(e) => onFilterOrFieldChange('personId', e.target.value)}
          disabled={!!(givenName || surname || reason)}
        />
      </Row>
    )
  }

  function searchActions() {
    return (
      <Row>
        <Cell columns={12}>
          <Row>
            <Button Icon={PersonSearch} onClick={searchBtnOnClickHandler} emphasis="high" disabled={searchBtnDisabled}>
              {t('action.search', 'Search')}
            </Button>
            <HistoricalSearchFiltersButton
              useBirthYearRange={useBirthYearRange}
              startBirthYearItem={startBirthYearItem}
              endBirthYearItem={endBirthYearItem}
              useDeathYearRange={useDeathYearRange}
              startDeathYearItem={startDeathYearItem}
              endDeathYearItem={endDeathYearItem}
              country={country}
              countries={countries}
              onFilterOrFieldChange={onFilterOrFieldChange}
            />
            <Button Icon={MenuClose} onClick={clearBtnOnClickHandler} emphasis="medium" disabled={clearBtnDisabled}>
              {t('action.clear', 'Clear')}
            </Button>
          </Row>
        </Cell>
      </Row>
    )
  }

  return (
    <>
      {fields(givenName, surname, personId, reason, onFilterOrFieldChange)}
      {searchActions(searchBtnDisabled, clearBtnDisabled, searchBtnOnClickHandler, clearBtnOnClickHandler)}
      <HistoricalSearchResultsDialog
        {...resultsOpener}
        persons={persons}
        removePersonOnDelete={removePersonOnDelete}
        updatePersonOnEdit={updatePersonOnEdit}
        countries={countries}
      />
    </>
  )
}

export default HistoricalSearch
